import { useHistory, useParams } from 'react-router-dom';
import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';
import { Button, Fade, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  useDocument_DownloadFilesMutation,
  useEstateWorkItem_GetEstateWorkItemByIdQuery,
} from 'queries';

import Spinner from 'common/spinner';
import HomeLayout from 'layouts/home-layout';
import { CustomDocViewerNoRendererError } from 'pages/common/document/document-preview-modal';
import { EstateDashboardTab } from 'pages/estate-dashboard/estate-dashboard';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

const useStyles = makeStyles(() => ({
  container: {
    backgroundColor: '#1C2541',
    color: '#fff',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: '800px',
    margin: 'auto',
    padding: '20px 0',
    height: '100%',
    width: '70%',
    maxWidth: '1200px',
  },
  actionButtonsContainer: {
    backgroundColor: '#1C2541',
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'space-between',
    marginTop: '20px',
  },
  goToTaskButton: {
    flex: 1,
    padding: '10px 0',
    borderRadius: '100px',
    color: '#1C2541',
    backgroundColor: '#5ED1F0',
    margin: '10px',
    '&:hover': {
      backgroundColor: '#5ED1F0',
      opacity: 0.8,
    },
  },
  backButton: {
    flex: 1,
    padding: '10px 0',
    borderRadius: '100px',
    color: '#fff',
    border: '2px solid #fff',
    margin: '10px',
    '&:hover': {
      border: '2px solid #fff',
    },
  },
  title: {
    fontWeight: 700,
    fontSize: '36px',
    marginTop: '18px',
    marginBottom: '20px',
    alignSelf: 'center',
  },
  subTitle: {
    fontWeight: 400,
    fontSize: '20px',
    marginTop: '18px',
    marginBottom: '20px',
    alignSelf: 'center',
  },
  docViewer: {
    flexGrow: 1,
    backgroundColor: '#fff',
    height: '100%',
  },
  contentClassName: {
    padding: 0,
  },
  downloadButton: {
    flex: 1,
    padding: '10px 0',
    borderRadius: '100px',
    border: '2px solid #E53DE8',
    margin: '10px',
    '&:hover': {
      backgroundColor: '#E53DE8',
      border: '2px solid #fff',
    },
    color: '#000000',
    backgroundColor: '#E53DE8',
  },
}));

const TemplateDocumentPage = () => {
  const classes = useStyles();
  const history = useHistory();
  const { portalId, estateWorkItemId } = useParams<Record<string, string>>();

  const { data: estateWorkItemData, loading: isEstateWorkItemDataLoading } =
    useEstateWorkItem_GetEstateWorkItemByIdQuery({
      fetchPolicy: 'network-only',
      variables: { input: { estateWorkItemId, portalId } },
      onError: (e) =>
        showAlert({
          message: `Error getting estate work item by ID: ${e.message}`,
          severity: NotificationSeverity.ERROR,
        }),
    });

  const [downloadFile] = useDocument_DownloadFilesMutation({
    onCompleted: ({ data: downloadFilesData }) => {
      window.open(downloadFilesData[0].signedUrl, '_blank');
    },
    onError: (e) => {
      showAlert({
        message: 'Error downloading files: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  if (isEstateWorkItemDataLoading) {
    return <Spinner />;
  }

  const document = estateWorkItemData?.data?.generatedDocument;

  if (!document) {
    return (
      <HomeLayout contentClassName={classes.contentClassName}>
        <Fade in>
          <div className={classes.container}>
            <div className={classes.content}>
              <Typography variant="h4" align="center" className={classes.title}>
                Document not found
              </Typography>
            </div>
          </div>
        </Fade>
      </HomeLayout>
    );
  }

  return (
    <HomeLayout contentClassName={classes.contentClassName}>
      <Fade in>
        <div className={classes.container}>
          <div className={classes.content}>
            <Typography variant="h4" align="center" className={classes.title}>
              Congratulations!
            </Typography>
            <Typography variant="h4" align="center" className={classes.subTitle}>
              A document has been generated for your review.
            </Typography>
            <div className={classes.docViewer}>
              <DocViewer
                documents={[
                  {
                    fileName: document.name,
                    uri: document.url,
                  },
                ]}
                pluginRenderers={DocViewerRenderers}
                prefetchMethod="GET"
                config={{
                  noRenderer: {
                    overrideComponent: CustomDocViewerNoRendererError,
                  },
                  pdfVerticalScrollByDefault: true,
                }}
                style={{ width: '100%', height: '800px' }}
              />
            </div>
            <div className={classes.actionButtonsContainer}>
              <Button
                type="button"
                variant="outlined"
                className={classes.backButton}
                onClick={() =>
                  history.replace(`/home/${portalId}/task/${estateWorkItemId}/template-data-wizard`)
                }
              >
                Back to Edit
              </Button>
              <Button
                type="button"
                variant="outlined"
                className={classes.downloadButton}
                onClick={() =>
                  downloadFile({
                    variables: {
                      portalId,
                      ids: [document.id],
                    },
                  })
                }
              >
                Download
              </Button>
              <Button
                type="button"
                variant="outlined"
                className={classes.goToTaskButton}
                onClick={() =>
                  history.replace(
                    `/home/${portalId}/estate/${estateWorkItemData.data.estateId}/estate-dashboard?tab=${EstateDashboardTab.ESTATE_CHECKLIST}`,
                  )
                }
              >
                Go to Task Overview
              </Button>
            </div>
          </div>
        </div>
      </Fade>
    </HomeLayout>
  );
};

export default TemplateDocumentPage;
